import type { ServiceDetailsPayload } from '@/components/workorders/billing/billingLineSource';
import { saveServiceDetails, type ServiceDetailsBase } from '@/components/workorders/billing/serviceDetailsConcurrency';
/**
 * Work Order Hooks
 *
 * Provides hooks for work order operations with TanStack Query.
 * Work orders are bookings with associated line items (parts, labor).
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { SupportedLanguage } from '@/lib/i18n';
import { queryKeys } from './queryKeys';
import { fromOffsetResponse, usePagedQuery } from './usePagedQuery';
import { useQuoteMutationRequest } from './useQuoteMutationRequest';
import type { SmallSuppliesSurcharge, WorkOrderDropoff } from '@/types';

export type { WorkOrderDropoff };

// API types
export type BookingStatus =
  | 'quote'
  | 'pending'
  | 'confirmed'
  | 'vehicle_arrived'
  | 'in_progress'
  | 'paused'
  | 'work_completed'
  | 'ready_to_invoice'
  | 'on_consolidated_invoice'
  | 'invoiced'
  | 'completed'
  | 'cancelled'
  | 'quote_rejected'
  // Legacy values — kept for backward compatibility
  | 'scheduled'
  | 'waiting_approval'
  | 'waiting_parts'
  | 'warranty_claim'  // legacy: now isWarranty flag
  | 'disputed'        // legacy: now isDisputed flag
  | 'invoicing_hold'; // legacy: now isInvoicingHold flag

export type BookingServiceStatus = 'pending' | 'in_progress' | 'paused' | 'completed';

type WorkOrderItemStatus = 'pending' | 'approved' | 'rejected' | 'completed';

export type LaborItemStatus = 'pending' | 'in_progress' | 'completed';

export type LaborTimeSource = 'catalog' | 'vehicle_specific' | 'provider' | 'manual' | 'actual';

/** Hand-set order status on a work-order part. Null on the wire means automatic (#3568). */
export type PartOrderStatus = 'ordered' | 'arrived' | 'backordered';

/** Effective part-need status after hand-set, purchase-order, and stock rules (#3568). */
export type PartNeedStatus =
  | 'needed'
  | 'reserved'
  | 'ordered'
  | 'backordered'
  | 'arrived'
  | 'installed'
  | 'dismissed';

export interface BookingServiceLaborItem {
  id: string;
  description: string;
  estimatedHours: number;
  /** Per-item discount, parity with BookingServicePart.discountPercent (#1814). */
  discountPercent: number;
  sortOrder: number;
  status: LaborItemStatus;
  startedAt: string | null;
  completedAt: string | null;
  completedBy: string | null;
  /** Origin of the snapshotted hours. Null on pre-#1441 rows. */
  timeSource?: LaborTimeSource | null;
  estimatedHoursOriginal?: number | null;
  editedBy?: string | null;
  editedAt?: string | null;
  editedByName?: string | null;
}

export interface BookingServicePart {
  id: string;
  partCategoryId: string | null;
  /** Inventory part the row is filled with; null for an unfilled slot or a hand-typed row (#3842). */
  partId: string | null;
  name: string;
  isRequired: boolean;
  quantity: number;
  /** Tax-exclusive net cents, derived server-side from unitPriceGrossCents (#1022). */
  unitPriceCents: number;
  /** Exact gross (VAT-included) price the user typed, in whole cents — the source of truth for display/editing. */
  unitPriceGrossCents: number;
  vatPercent: number;
  discountPercent: number;
  notes: string | null;
  sortOrder: number;
  /** Hand-set order status. Null means automatic. Older servers omit it (#3568). */
  orderStatus?: PartOrderStatus | null | undefined;
  orderStatusSetAt?: string | null | undefined;
}

export interface BookingServiceExtra {
  id: string;
  description: string;
  /** Tax-exclusive net cents, derived server-side from amountGrossCents (#1600). */
  amountCents: number;
  /** Exact VAT-inclusive amount the user typed. */
  amountGrossCents: number;
  isTaxable: boolean;
  hasVariableQuantity: boolean;
  unit: string | null;
  quantity: number | null;
  sortOrder: number;
}

export interface WorkOrderItem {
  id: string;
  bookingId: string;
  description: string;
  category: string | null;
  partsCostCents: number;
  laborCostCents: number;
  estimatedHours: number | null;
  status: WorkOrderItemStatus;
  approvalRequired: boolean;
  approvedAt: string | null;
  approvedBy: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkOrderListItem {
  requiresStaffReview?: boolean;
  workOrderNumber?: string | null;
  id: string;
  customerId: string;
  vehicleId: string;
  scheduledAt: string;
  customerRequestedPickupAt: string | null;
  estimatedDurationHours: number | null;
  /** Closed clocked hours (#2705). Open timers are in actualActiveHours. */
  actualDurationHours?: number;
  actualActiveHours?: number;
  unassignedActualDurationHours?: number;
  unassignedActualActiveHours?: number;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: BookingStatus;
  customerNotes: string | null;
  internalNotes: string | null;
  managerNotes: string | null;
  assignedMechanicId: string | null;
  mechanicScheduledStart: string | null;
  mechanicScheduledEnd: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  pauseReason: string | null;
  /** Expected continuation time when paused; null = "reschedule later" / no plan (#548). */
  expectedResumeAt: string | null;
  completedAt: string | null;
  /** The rest of the dates the list's date column and tooltip show (#3765). */
  vehicleDropoffAt?: string | null;
  quoteExpiresAt?: string | null;
  /** Days from the first send (#4120). Absent on older payloads. */
  quoteValidityDays?: number | null;
  /** Effective expiry. Null while an unsent quote only has a day count. */
  effectiveQuoteExpiresAt?: string | null;
  confirmedAt?: string | null;
  signedAt?: string | null;
  invoicedAt?: string | null;
  totalCostCents: number | null;
  createdAt: string;
  updatedAt: string;
  customerName: string;
  customerPhone: string;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  mechanicName: string | null;
  itemCount: number;
  services: {
    id: string;
    name: string;
    bookingServiceId: string;
    status: BookingServiceStatus;
    assignedMechanicId: string | null;
    mechanicName: string | null;
    actualDurationHours?: number;
    actualActiveHours?: number;
  }[];
  totalPartsCents: number;
  totalLaborCents: number;
  vatPercent?: number;
  taxCents?: number;
  totalWithVatCents?: number;
  smallSuppliesSurcharge?: SmallSuppliesSurcharge | null;
  isWarranty?: boolean;
  isDisputed?: boolean;
  isInvoicingHold?: boolean;
  isWaitingForParts?: boolean;
  /**
   * Derived approval wait (#3027): the work order has open approval requests.
   * Sits alongside the primary status; absent on payloads from older API pods.
   */
  isWaitingForApproval?: boolean;
  /** Number of open approval requests behind `isWaitingForApproval`; absent means 0. */
  openApprovalCount?: number;
  /** System the work order was imported from (`innovoice`); null when made here (#3366). */
  legacySource?: string | null;
}

/** Reception-question answers snapshotted onto the work order at create (#2130). */
export interface AdditionalQuestionAnswer {
  questionId: string;
  label: string;
  answer: string;
}

export interface WorkOrderDetail {
  requiresStaffReview?: boolean;
  workOrderNumber?: string | null;
  id: string;
  customerId: string;
  vehicleId: string;
  scheduledAt: string;
  vehicleDropoffAt?: string | null;
  /** 'exact' pins the work to scheduledAt; 'floating' queues it within the day (#1138). */
  schedulingType?: 'exact' | 'floating';
  customerRequestedPickupAt: string | null;
  estimatedDurationHours: number | null;
  /** Closed clocked hours (#2705). Open timers are in actualActiveHours. */
  actualDurationHours?: number;
  actualActiveHours?: number;
  unassignedActualDurationHours?: number;
  unassignedActualActiveHours?: number;
  /** NULL kun työmääräimellä ei ole palveluita — ks. booking.schema.ts:55. */
  serviceType: string | null;
  status: BookingStatus;
  customerNotes: string | null;
  internalNotes: string | null;
  managerNotes: string | null;
  assignedMechanicId: string | null;
  mechanicScheduledStart: string | null;
  mechanicScheduledEnd: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  /** Stored expiry column (#849). Legacy rows; a days-quote keeps this null. */
  quoteExpiresAt: string | null;
  /** Days from the first send (#4120). Null on a legacy absolute date. */
  quoteValidityDays?: number | null;
  /** Expiry after the first send. Null while the quote has not been sent. */
  effectiveQuoteExpiresAt?: string | null;
  /** Latest send-out of this quote and the customer's answer (#2651); null when never sent. */
  quoteSend?: QuoteSendSummary | null;
  startedAt: string | null;
  /** Expected continuation time when paused; null = "reschedule later" / no plan (#548). */
  expectedResumeAt: string | null;
  /** Calendar layout for this work order's services (#544). */
  serviceScheduling: 'sequential' | 'parallel';
  completedAt: string | null;
  totalCostCents: number | null;
  createdAt: string;
  updatedAt: string;
  customerWaits?: boolean;
  allowMinorMaintenance?: boolean;
  jointInspectionRequested?: boolean;
  preapprovedAdditionalWorkLimitCents?: number | null;
  contactPerson?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  /** Role-based parties (epic #763/#833) — the orderer is the work order's "customer". */
  parties?: {
    role: string;
    partyType: 'person' | 'company';
    personId: string | null;
    companyId: string | null;
    name: string;
    phone: string | null;
    email: string | null;
    invoiceEmail?: string | null;
    /** The person's language; null for a company (#3632). */
    preferredLanguage?: string | null;
  }[];
  customer: {
    id: string;
    customerType?: 'individual' | 'business';
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    businessName?: string | null;
  };
  vehicle: {
    id: string;
    registrationNumber: string;
    make: string | null;
    model: string | null;
    year: number | null;
    mileage: number | null;
    /** Sent since #3383. */
    vin?: string | null;
  };
  mechanic: {
    id: string;
    firstName: string | null;
    lastName: string | null;
  } | null;
  services: {
    id: string;
    bookingServiceId: string;
    /** Catalog name: the heading on every view (#3655). */
    name: string;
    /** Work-specific title under the catalog name (#3655). */
    title?: string | null;
    /** Catalog pricing. Fixed-price jobs keep catalog/override price when hours change. */
    pricingType?: string;
    /** Catalog fixed price, net (#1983). */
    fixedPriceCents?: number | null;
    /** Work-order net override; null = catalog (#1983). */
    fixedPriceOverrideCents?: number | null;
    /** Planned hours for this service (#2667). Optional so older payloads still typecheck. */
    estimatedDurationHours?: number | null;
    /** True when planned hours were set by the user; false/omitted means derived (#2667). */
    plannedHoursManual?: boolean;
    /** Closed clocked hours for this service (#2705). */
    actualDurationHours?: number;
    actualActiveHours?: number;
    assignedMechanicId: string | null;
    status: BookingServiceStatus;
    startedAt: string | null;
    pausedAt: string | null;
    completedAt: string | null;
    /** Technician remarks for this task (#1754) — customer-facing, shown on the invoice/print. */
    notes: string | null;
    mechanic: { id: string; firstName: string | null; lastName: string | null } | null;
    laborItems: BookingServiceLaborItem[];
    parts: BookingServicePart[];
    extras: BookingServiceExtra[];
  }[];
  items: WorkOrderItem[];
  totalPartsCents: number;
  totalLaborCents: number;
  /** Snapshotted hourly labor rate, tax-exclusive (#1437). */
  laborRateCents?: number | null;
  /** VAT-inclusive hourly rate the user typed (#1600). */
  laborRateGrossCents?: number | null;
  // VAT breakdown for the printout (#843). totalCostCents is the net total.
  vatPercent?: number;
  taxCents?: number;
  totalWithVatCents?: number;
  smallSuppliesSurcharge?: SmallSuppliesSurcharge | null;
  /** Surcharge amount (net cents) already included in the totals above (#2644). */
  surchargeCents?: number;
  isWarranty?: boolean;
  isDisputed?: boolean;
  isInvoicingHold?: boolean;
  isWaitingForParts?: boolean;
  /**
   * Derived approval wait (#3027): the work order has open approval requests.
   * Sits alongside the primary status; absent on payloads from older API pods.
   */
  isWaitingForApproval?: boolean;
  /** Number of open approval requests behind `isWaitingForApproval`; absent means 0. */
  openApprovalCount?: number;
  cancellationReason?: string | null;
  disputeReason?: string | null;
  invoicingHoldReason?: string | null;
  relatedBookingId?: string | null;
  vehicleLocation: string | null;
  vehicleLocationDetail: string | null;
  assignedLiftId: string | null;
  pauseLocation: string | null;
  serviceBookChecked?: boolean;
  serviceBookFilled?: boolean;
  serviceBookType?: string | null;
  finalInspectionCompleted?: boolean;
  finalInspectionCompletedAt?: string | null;
  finalInspectionCompletedBy?: string | null;
  /** Reception-question answers snapshotted at create (#2130). */
  additionalQuestionAnswers?: AdditionalQuestionAnswer[];
  /** Latest customer-reported vehicle dropoff (PF-135). Null when none. */
  dropoff?: WorkOrderDropoff | null;
}

interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    nextCursor: string | null;
    prevCursor: string | null;
    hasMore: boolean;
  };
}

export interface WorkOrderFilters {
  [key: string]: string | number | undefined;
  q?: string;
  /** Single status or comma-separated list (e.g. "in_progress,waiting_parts") */
  status?: string;
  mechanicId?: string;
  customerId?: string;
  vehicleId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'scheduledAt' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
}

/** Filters for the page-number list. Page size and sort are sent by the paged hook. */
export interface WorkOrderPageFilters {
  q?: string;
  status?: string;
  mechanicId?: string;
  customerId?: string;
  vehicleId?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Stats response from GET /work-orders/stats
export interface WorkOrderStats {
  totalCount: number;
  byStatus: { status: string; count: number }[];
  /**
   * Orders waiting for approval: derived flag or legacy status, counted once.
   * Not part of `totalCount`. Absent from API pods older than #3027.
   */
  waitingApprovalCount?: number | undefined;
  scheduledToday: number;
  completedThisWeek: number;
}

export interface WorkOrderStatsFilters {
  [key: string]: string | undefined;
  /** Free-text search, same semantics as the list's `q`. */
  q?: string;
  /** Comma-separated mechanic ids, same as the list's `mechanicId`. */
  mechanicId?: string;
}

/**
 * Hook to fetch work order statistics.
 *
 * Pass the list's active filters so the status chip counts describe the same
 * set of work orders as the list under them (#2154).
 */
export function useWorkOrderStats(filters: WorkOrderStatsFilters = {}) {
  const hasFilters = Boolean(filters.q) || Boolean(filters.mechanicId);
  return useQuery({
    queryKey: hasFilters
      ? queryKeys.workOrders.stats(filters)
      : queryKeys.workOrders.stats(),
    queryFn: () => api.get<WorkOrderStats>('/work-orders/stats', { params: filters }),
    staleTime: 60 * 1000, // 1 minute
  });
}

interface CreateWorkOrderItemData {
  description: string;
  category?: string;
  partsCostCents: number;
  laborCostCents: number;
  estimatedHours?: number;
  approvalRequired?: boolean;
}

interface UpdateWorkOrderItemData {
  description?: string;
  category?: string;
  partsCostCents?: number;
  laborCostCents?: number;
  estimatedHours?: number;
  status?: WorkOrderItemStatus;
}

/**
 * Hook to fetch paginated list of work orders with infinite scroll
 */
export function useWorkOrders(
  filters: WorkOrderFilters = {},
  options?: { enabled?: boolean },
) {
  return useInfiniteQuery({
    queryKey: queryKeys.workOrders.list(filters),
    queryFn: ({ pageParam }) =>
      api.get<PaginatedResponse<WorkOrderListItem>>('/work-orders', {
        params: {
          ...filters,
          cursor: pageParam,
        },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 15 * 1000, // 15 seconds
    enabled: options?.enabled ?? true,
  });
}

/**
 * Sort keys the list route accepts on the offset list (staff-api
 * `WORK_ORDER_LIST_SORT_FIELDS`). A column outside this list is never sent: the
 * server answers 400 to it, and sorting only the loaded page would mislead (#4128).
 */
export const WORK_ORDER_LIST_SORT_KEYS = [
  'scheduledAt',
  'status',
  'serviceType',
  'statusDate',
  'workOrderNumber',
] as const;

export type WorkOrderListSortKey = (typeof WORK_ORDER_LIST_SORT_KEYS)[number];

export function isWorkOrderListSortKey(key: string): key is WorkOrderListSortKey {
  return (WORK_ORDER_LIST_SORT_KEYS as readonly string[]).includes(key);
}

/**
 * Page-number list for the work orders page (#2739).
 * Always sends `limit` and `offset` (including 0) so the route picks offset mode.
 * The infinite hook above stays for the other consumers.
 */
export function usePagedWorkOrders(
  filters: WorkOrderPageFilters = {},
  options?: { enabled?: boolean },
) {
  return usePagedQuery({
    queryKey: queryKeys.workOrders.lists(),
    filters,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
    defaultSort: { sortBy: 'scheduledAt', sortDir: 'desc' },
    staleTime: 15_000,
    fetchPage: async ({ filters: pageFilters, limit, offset, sortBy, sortDir }) => {
      const params: Record<string, string | number | undefined> = {
        ...pageFilters,
        limit,
        offset,
      };
      if (sortBy !== undefined) params.sortBy = sortBy;
      if (sortDir !== undefined) params.sortDir = sortDir;
      const res = await api.get<{
        data: WorkOrderListItem[];
        pagination: { total: number };
      }>('/work-orders', { params });
      return fromOffsetResponse(res);
    },
  });
}

/**
 * Hook to fetch a single work order by ID
 */
export function useWorkOrder(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.detail(id ?? ''),
    queryFn: () => api.get<WorkOrderDetail>(`/work-orders/${id}`),
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Line-item edits change cost totals and the invoice preview for this work
 * order only (booking and work order share the same ID) (#2643).
 */
function invalidateWorkOrderItemViews(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrderId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.workOrders.detail(workOrderId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.workOrders.lists(),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.bookings.detail(workOrderId),
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.invoicePreview(workOrderId),
  });
}

/**
 * Hook to add an item to a work order
 */
export function useAddWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      data,
    }: {
      workOrderId: string;
      data: CreateWorkOrderItemData;
    }) => api.post<WorkOrderItem>(`/work-orders/${workOrderId}/items`, data),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrderItemViews(queryClient, workOrderId);
    },
  });
}

/**
 * Hook to update a work order item
 */
export function useUpdateWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
      data,
    }: {
      workOrderId: string;
      itemId: string;
      data: UpdateWorkOrderItemData;
    }) =>
      api.patch<WorkOrderItem>(
        `/work-orders/${workOrderId}/items/${itemId}`,
        data
      ),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrderItemViews(queryClient, workOrderId);
    },
  });
}

/**
 * Hook to delete a work order item
 */
export function useDeleteWorkOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      itemId,
    }: {
      workOrderId: string;
      itemId: string;
    }) => api.delete(`/work-orders/${workOrderId}/items/${itemId}`),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrderItemViews(queryClient, workOrderId);
    },
  });
}

export interface MyServiceTask {
  bookingServiceId: string;
  serviceId: string;
  serviceName: string;
  status: BookingServiceStatus;
  startedAt: string | null;
  pausedAt: string | null;
  completedAt: string | null;
  workOrderId: string;
  workOrderStatus: BookingStatus;
  scheduledAt: string;
  vehicleRegNumber: string;
  vehicleMakeModel: string;
  customerName: string;
  assignedMechanicId: string | null;
  mechanicName: string | null;
}

interface MyServiceFilters {
  [key: string]: string | undefined;
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  userId?: string;
}

function invalidateMyServices(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.myServices() });
}

function invalidateWorkOrder(queryClient: ReturnType<typeof useQueryClient>, id: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(id) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id) });
  // Labor-hour edits change the invoice preview; keep the Payments card in sync (#1438).
  void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(id) });
  // Duration/schedule saves must refresh calendar bars without waiting for the 60s poll (#2667 C5).
  void queryClient.invalidateQueries({ queryKey: [...queryKeys.bookings.all, 'calendar'] });
  // Timer start/pause/resume/complete and labor-item status must refresh the
  // mechanic's my-services list without waiting for its 15s staleTime (#2936).
  invalidateMyServices(queryClient);
}

/**
 * Hook to start work on a work order
 */
/**
 * `mechanicId` (#3281): whose timer starts. Omitted = the caller. The Tasks
 * page sends the viewed person when a supervisor acts on someone else's day.
 */
export type StartWorkOrderInput = string | {
  id: string;
  bookingServiceId?: string | null;
  description?: string;
  mechanicId?: string;
};

function workOrderActionId(input: string | { id: string }): string {
  return typeof input === 'string' ? input : input.id;
}

function startWorkOrderBody(input: StartWorkOrderInput): {
  bookingServiceId?: string | null;
  description?: string;
  mechanicId?: string;
} {
  if (typeof input === 'string') return {};
  const body: { bookingServiceId?: string | null; description?: string; mechanicId?: string } = {};
  if (input.bookingServiceId !== undefined) body.bookingServiceId = input.bookingServiceId;
  if (input.description !== undefined) body.description = input.description;
  if (input.mechanicId !== undefined) body.mechanicId = input.mechanicId;
  return body;
}

export function useStartWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: StartWorkOrderInput) => {
      const id = workOrderActionId(input);
      return api.post(`/work-orders/${id}/start`, startWorkOrderBody(input));
    },
    onSuccess: (_, input) => invalidateWorkOrder(queryClient, workOrderActionId(input)),
  });
}

/**
 * Hook to pause work on a work order
 */
export function usePauseWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      reason,
      pauseLocation,
      parkingZoneId,
      parkingSpotId,
      expectedResumeAt,
      mechanicId,
    }: {
      id: string;
      reason?: string;
      pauseLocation?: 'lift' | 'parking';
      parkingZoneId?: string;
      parkingSpotId?: string;
      expectedResumeAt?: string | null;
      /** Whose timer closes (#3281); omitted = the caller. */
      mechanicId?: string;
    }) =>
      api.post(`/work-orders/${id}/pause`, {
        reason,
        pauseLocation,
        parkingZoneId,
        parkingSpotId,
        expectedResumeAt,
        ...(mechanicId !== undefined ? { mechanicId } : {}),
      }),
    onSuccess: (_, { id }) => {
      invalidateWorkOrder(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.parking.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.lifts.status() });
    },
  });
}

/**
 * Hook to resume work on a work order
 */
export type ResumeWorkOrderInput = string | {
  id: string;
  bookingServiceId?: string | null;
  /** Whose timer resumes (#3281); omitted = the caller. */
  mechanicId?: string;
};

function resumeWorkOrderBody(input: ResumeWorkOrderInput): {
  bookingServiceId?: string | null;
  mechanicId?: string;
} {
  if (typeof input === 'string') return {};
  const body: { bookingServiceId?: string | null; mechanicId?: string } = {};
  if (input.bookingServiceId !== undefined) body.bookingServiceId = input.bookingServiceId;
  if (input.mechanicId !== undefined) body.mechanicId = input.mechanicId;
  return body;
}

export function useResumeWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: ResumeWorkOrderInput) => {
      const id = workOrderActionId(input);
      return api.post(`/work-orders/${id}/resume`, resumeWorkOrderBody(input));
    },
    onSuccess: (_, input) => {
      invalidateWorkOrder(queryClient, workOrderActionId(input));
      void queryClient.invalidateQueries({ queryKey: queryKeys.parking.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.lifts.status() });
    },
  });
}

/**
 * Undo the last status change on a work order
 */
export function useUndoWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, targetHistoryId, reason }: { id: string; targetHistoryId?: string; reason: string }) =>
      api.post<{ success: boolean; previousStatus: string; currentStatus: string; historyId: string }>(
        `/work-orders/${id}/undo`,
        { targetHistoryId, reason },
      ),
    onSuccess: (_, { id }) => {
      invalidateWorkOrder(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: ['work-orders', id, 'history'] });
    },
  });
}

/**
 * Foreman acknowledge of a customer-reported vehicle dropoff (PF-135).
 * Needs `scheduling.exceptions`. Marks related staff notifications read.
 */
export function useAcknowledgeDropoff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workOrderId: string) =>
      api.post<{ success: true; dropoff: WorkOrderDropoff }>(
        `/work-orders/${workOrderId}/dropoff/acknowledge`,
      ),
    onSuccess: (_, workOrderId) => {
      invalidateWorkOrder(queryClient, workOrderId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
}

// Vehicle location types
export type VehicleLocationType = 'arrival' | 'on_lift' | 'parking' | 'ready' | 'delivered';

export interface VehicleLocationHistoryEntry {
  id: string;
  bookingId: string;
  locationType: VehicleLocationType;
  locationZone: string | null;
  locationDetail: string | null;
  locationZoneId: string | null;
  locationSpotId: string | null;
  locationLiftId: string | null;
  recordedAt: string;
  recordedBy: string | null;
}

/**
 * Hook to update vehicle location on a work order
 */
export function useUpdateVehicleLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        locationType: VehicleLocationType;
        locationZone?: string;
        locationDetail?: string;
        locationZoneId?: string;
        locationSpotId?: string;
        locationLiftId?: string;
      };
    }) => api.patch(`/work-orders/${id}/vehicle-location`, data),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to fetch vehicle location history
 */
export function useVehicleLocationHistory(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.workOrders.detail(id ?? ''), 'vehicleLocationHistory'],
    queryFn: () =>
      api.get<VehicleLocationHistoryEntry[]>(
        `/work-orders/${id}/vehicle-location-history`
      ),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

/**
 * Hook to complete work on a work order
 */
export function useCompleteWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, mileage }: { id: string; mileage?: number }) =>
      api.post(`/work-orders/${id}/complete`, mileage != null ? { mileage } : {}),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Record an odometer reading on a work order (POST /work-orders/:id/mileage).
 * Used by the work-completion dialog (#4101) so the reading taken when the
 * work is done is its own record, not the one entered at intake.
 */
export function useRecordWorkOrderMileage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, mileage }: { id: string; vehicleId: string; mileage: number }) =>
      api.post<{ success: boolean; mileage: number }>(`/work-orders/${id}/mileage`, { mileage }),
    onSuccess: (_, { id, vehicleId }) => {
      invalidateWorkOrder(queryClient, id);
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.mileage(vehicleId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.vehicles.mileageHistory(vehicleId) });
    },
  });
}

/**
 * Hook to fetch mechanic's assigned service tasks
 */
export function useMyServiceTasks(
  filters: MyServiceFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workOrders.myServices(filters),
    queryFn: () =>
      api.get<{ data: MyServiceTask[] }>('/work-orders/my-services', {
        params: filters,
      }),
    staleTime: 15 * 1000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to start a single service
 */
export function useStartService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, serviceId }: { workOrderId: string; serviceId: string }) =>
      api.post(`/work-orders/${workOrderId}/services/${serviceId}/start`, {}),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to pause a single service
 */
export function usePauseService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      serviceId,
      expectedResumeAt,
    }: {
      workOrderId: string;
      serviceId: string;
      // Continuation plan for the work order when this pause tips it into paused
      // (null = reschedule later / no plan) — #548.
      expectedResumeAt?: string | null;
    }) =>
      api.post(`/work-orders/${workOrderId}/services/${serviceId}/pause`, { expectedResumeAt }),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to reopen a completed service (#3403): back under way, optional reason.
 */
export function useReopenService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, serviceId, reason }: { workOrderId: string; serviceId: string; reason?: string }) =>
      api.post(`/work-orders/${workOrderId}/services/${serviceId}/reopen`, reason ? { reason } : {}),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to resume a paused service
 */
export function useResumeService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, serviceId }: { workOrderId: string; serviceId: string }) =>
      api.post(`/work-orders/${workOrderId}/services/${serviceId}/resume`, {}),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to complete a single service
 */
export function useCompleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workOrderId, serviceId, mileage }: { workOrderId: string; serviceId: string; mileage?: number }) =>
      api.post(`/work-orders/${workOrderId}/services/${serviceId}/complete`, mileage != null ? { mileage } : {}),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to mark vehicle as arrived
 */
export function useVehicleArrived() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, customerConfirmed, confirmationMethod }: { id: string; customerConfirmed?: boolean; confirmationMethod?: string }) =>
      api.post(`/work-orders/${id}/vehicle-arrived`, { customerConfirmed, confirmationMethod }),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to mark work order as ready to invoice
 */
export function useReadyToInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.post(`/work-orders/${id}/ready-to-invoice`, {}),
    onSuccess: (_, id) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to reject a quote
 */
export function useRejectQuote() {
  const queryClient = useQueryClient();
  const postQuote = useQuoteMutationRequest();
  return useMutation({
    mutationFn: ({ id, expectedUpdatedAt }: { id: string; expectedUpdatedAt: string }) =>
      postQuote(`/work-orders/${id}/reject-quote`, {}, expectedUpdatedAt),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
    onError: (error, { id }) => {
      if (error instanceof ApiError && error.status === 412) {
        invalidateWorkOrder(queryClient, id);
      }
    },
  });
}

/** One send-out of a quotation (#2651), as GET /work-orders/:id and the send route return it. */
export interface QuoteSendSummary {
  id: string;
  sentAt: string;
  sentVia: 'email' | 'whatsapp' | string;
  recipient: string;
  sentByName: string | null;
  tokenExpiresAt: string;
  respondedAt: string | null;
  response: 'accepted' | 'rejected' | 'partial' | null;
  rejectionReason: string | null;
  customerComment: string | null;
}

export interface SendQuoteData {
  channel: 'email' | 'whatsapp';
  recipientEmail?: string;
  recipientPhone?: string;
  message?: string;
  /** Language of the email and the PDF (#3632). */
  language?: SupportedLanguage;
}

/** Send a quotation to the customer with a response link (#2651). */
export function useSendQuote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SendQuoteData }) =>
      api.post<{ success: boolean; quoteSend: QuoteSendSummary | null }>(`/work-orders/${id}/send-quote`, data),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to mark work order as disputed
 */
export function useDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/work-orders/${id}/dispute`, { reason }),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to resolve a dispute
 */
export function useResolveDispute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/work-orders/${id}/resolve-dispute`, {}),
    onSuccess: (_, id) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to mark work order as warranty claim
 */
export function useMarkWarranty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, relatedBookingId }: { id: string; relatedBookingId?: string }) =>
      api.post(`/work-orders/${id}/warranty`, { relatedBookingId }),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to put work order on invoicing hold
 */
export function useInvoicingHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/work-orders/${id}/invoicing-hold`, { reason }),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to resolve invoicing hold
 */
export function useResolveInvoicingHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/work-orders/${id}/resolve-invoicing-hold`, {}),
    onSuccess: (_, id) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to set waiting for parts flag
 */
export function useSetWaitingForParts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/work-orders/${id}/waiting-for-parts`, {}),
    onSuccess: (_, id) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to clear waiting for parts flag (parts received)
 */
export function usePartsReceived() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/work-orders/${id}/parts-received`, {}),
    onSuccess: (_, id) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Hook to cancel a work order
 */
export function useCancelWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post(`/work-orders/${id}/cancel`, { reason }),
    onSuccess: (_, { id }) => invalidateWorkOrder(queryClient, id),
  });
}

/**
 * Permanently delete a work order (#3343) — for duplicates and test work
 * orders. The server refuses with 409 and a `code` when an invoice, a
 * signature, logged hours or stock movements exist.
 */
export function useDeleteWorkOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/work-orders/${id}`),
    onSuccess: (_, id) => {
      // The detail no longer exists; drop it instead of refetching a 404.
      queryClient.removeQueries({ queryKey: queryKeys.workOrders.detail(id) });
      queryClient.removeQueries({ queryKey: queryKeys.bookings.detail(id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.lists() });
      void queryClient.invalidateQueries({ queryKey: [...queryKeys.bookings.all, 'calendar'] });
      invalidateMyServices(queryClient);
    },
  });
}

/**
 * Hook to update booking service details (labor items, parts, extras)
 */
export function useUpdateBookingServiceDetails(bookingServiceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    scope: { id: `booking-service-details-${bookingServiceId}` },
    mutationFn: (input: { workOrderId: string; bookingServiceId: string; data: ServiceDetailsPayload; base: ServiceDetailsBase }) => saveServiceDetails(input),
    // The legacy summary in booking lists names the catalog services, so a
    // title edit no longer changes it (#3655).
    onSuccess: (_, { workOrderId }) => invalidateWorkOrder(queryClient, workOrderId),
    onError: (_, { workOrderId }) => invalidateWorkOrder(queryClient, workOrderId),
  });
}

/**
 * Copy a service's closed actual hours onto its labor-item billable hours (#2706).
 * Does not write planned/manual latch or time-entry rows; the API owns that.
 */
export function useBillActualHours() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      bookingServiceId,
    }: {
      workOrderId: string;
      bookingServiceId: string;
    }) => api.post(`/work-orders/${workOrderId}/services/${bookingServiceId}/bill-actual`, {}),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      void queryClient.invalidateQueries({ queryKey: ['workOrderParts', workOrderId] });
    },
  });
}

// ─── Parts Order Hooks ───────────────────────────────────────────

export interface PartsStatusResponse {
  neededParts: Array<{
    id: string;
    /** Null when the free-text booking part does not match a catalog row (#1110). */
    partId: string | null;
    partName: string;
    partSku: string;
    quantity: number;
    unitPriceCents: number;
    /** Available-to-reserve stock (on-hand minus already-reserved) across all warehouses; null if this part has no resolved catalog match (#727). */
    availableStock: number | null;
    /** Effective status. Older servers omit it (#3568). */
    status?: PartNeedStatus | undefined;
    /** Hand-set status; null means automatic. Older servers omit it (#3568). */
    orderStatus?: PartOrderStatus | null | undefined;
    orderStatusSetAt?: string | null | undefined;
    /** Status implied by the matched purchase-order line, if any (#3568). */
    purchaseOrderStatus?: PartOrderStatus | null | undefined;
  }>;
  onOrderParts: Array<{
    lineId: string;
    partId: string;
    partName: string;
    partSku?: string;
    quantityOrdered: number;
    quantityReceived: number;
    unitPriceCents: number;
    poId: string;
    poOrderNumber: string;
    poStatus: string;
    expectedDeliveryDate: string | null;
    supplierId: string;
    supplierName: string;
  }>;
  /** Active (reserved/picked) stock_reservations holds for this work order (#727). */
  reservations: {
    reservationId: string;
    partId: string;
    partNumber: string;
    partName: string;
    quantity: number;
    status: string;
    warehouseId: string;
    reservedAt: string;
  }[];
  /**
   * The work order's own part rows still awaiting a consumption confirmation
   * (#3270). `workOrderPartId` is the only id confirm-parts-consumption
   * accepts — not neededParts[].id nor onOrderParts[].lineId.
   */
  consumableParts: {
    workOrderPartId: string;
    partId: string | null;
    partName: string;
    partSku: string;
    quantity: number;
    unit: string;
    consumptionStatus: string;
    purchaseOrderLineId: string | null;
  }[];
  isWaitingForParts: boolean;
}

/** The wire shape: an API pod older than #3270 sends no `consumableParts`. */
export type PartsStatusWireResponse = Omit<PartsStatusResponse, 'consumableParts'> & {
  consumableParts?: PartsStatusResponse['consumableParts'];
};

/**
 * Normalise a parts-status response. A missing `consumableParts` (older API
 * pod during a rollout) means "nothing confirmable", never a fallback to
 * booking-service part or PO line ids (#3270). The spread keeps every other
 * field, including hand-set order status on neededParts (#3568).
 */
export function normalizePartsStatus(response: PartsStatusWireResponse): PartsStatusResponse {
  return { ...response, consumableParts: response.consumableParts ?? [] };
}

/**
 * Hook to fetch parts order status for a work order
 */
export function useWorkOrderPartsStatus(workOrderId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workOrders.partsStatus(workOrderId ?? ''),
    queryFn: async () =>
      normalizePartsStatus(await api.get<PartsStatusWireResponse>(`/work-orders/${workOrderId}/parts-status`)),
    enabled: !!workOrderId,
    staleTime: 15_000,
  });
}

interface OrderPartsInput {
  parts: Array<{
    partId: string;
    quantity: number;
    supplierId?: string;
  }>;
  supplierId?: string;
  notes?: string | null;
}

interface OrderPartsResponse {
  success: boolean;
  purchaseOrders: Array<{
    id: string;
    orderNumber: string;
    supplierId: string;
    expectedDeliveryDate: string;
  }>;
  estimatedArrival: string;
  taskId: string;
}

/**
 * Hook to order parts for a work order (creates POs + waiting_parts task)
 */
export function useOrderPartsForWorkOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      data,
    }: {
      workOrderId: string;
      data: OrderPartsInput;
    }) => api.post<OrderPartsResponse>(`/work-orders/${workOrderId}/order-parts`, data),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.all });
    },
  });
}

/**
 * Hand-set a part row's order status, or send null to return it to automatic (#3568).
 * parts-status lives under the work-order detail key, so that one invalidation covers it.
 */
export function useUpdatePartOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      bookingServiceId,
      partId,
      orderStatus,
    }: {
      workOrderId: string;
      bookingServiceId: string;
      partId: string;
      orderStatus: PartOrderStatus | null;
    }) =>
      api.patch(
        `/work-orders/${workOrderId}/services/${bookingServiceId}/parts/${partId}/order-status`,
        { orderStatus },
      ),
    onSuccess: (_, { workOrderId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.partsDashboard() });
    },
  });
}

// ============================================================================
// LABOR ITEM STATUS MANAGEMENT
// ============================================================================

/**
 * Hook to update labor item status
 */
export function useUpdateLaborItemStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      serviceId,
      itemId,
      status,
    }: {
      workOrderId: string;
      serviceId: string;
      itemId: string;
      status: LaborItemStatus;
    }) =>
      api.patch(
        `/work-orders/${workOrderId}/services/${serviceId}/labor-items/${itemId}/status`,
        { status }
      ),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
    },
  });
}

/**
 * Hook to mark labor item as completed
 */
export function useCompleteLaborItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      serviceId,
      itemId,
    }: {
      workOrderId: string;
      serviceId: string;
      itemId: string;
    }) =>
      api.post(
        `/work-orders/${workOrderId}/services/${serviceId}/labor-items/${itemId}/complete`
      ),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
    },
  });
}

/**
 * Hook to mark labor item as in progress
 */
export function useStartLaborItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workOrderId,
      serviceId,
      itemId,
    }: {
      workOrderId: string;
      serviceId: string;
      itemId: string;
    }) =>
      api.post(
        `/work-orders/${workOrderId}/services/${serviceId}/labor-items/${itemId}/start`
      ),
    onSuccess: (_, { workOrderId }) => {
      invalidateWorkOrder(queryClient, workOrderId);
    },
  });
}

/** How the labour of a job is printed on the work-order PDF (#4107). */
export type WorkOrderPdfLabourDetail = 'collapsed' | 'itemized';

/** `delivery_note` is the price-free "Lähete" (#4111). */
export type WorkOrderPdfVariant = 'work_order' | 'delivery_note';

export interface WorkOrderPdfOptions {
  labourDetail?: WorkOrderPdfLabourDetail;
  variant?: WorkOrderPdfVariant;
}

/** Server path of the work-order PDF; only options that were chosen go on the query. */
export function workOrderPdfPath(workOrderId: string, options: WorkOrderPdfOptions = {}): string {
  const params = new URLSearchParams();
  if (options.labourDetail) params.set('labourDetail', options.labourDetail);
  if (options.variant) params.set('variant', options.variant);
  const query = params.toString();
  return `/work-orders/${workOrderId}/pdf${query ? `?${query}` : ''}`;
}

/** A 200 that is not a PDF must not end up as a blank tab (#3116). */
function assertWorkOrderPdfBlob(blob: Blob): void {
  if (blob.size === 0 || !blob.type.startsWith('application/pdf')) {
    throw new ApiError(502, 'Bad Gateway', { code: 'pdf_invalid', contentType: blob.type, size: blob.size });
  }
}

/**
 * Open the server A4 work-order PDF and trigger print (#1714).
 *
 * `preOpenedWindow` is a window the click handler opened synchronously, before
 * any await, so the popup blocker still counts it as the user's gesture
 * (#4093). `null` means that open was blocked: the PDF falls back to a link
 * click. Omitted keeps the old behaviour of opening the window after the download.
 * Any failure closes a pre-opened window and is rethrown for the caller to report.
 */
export async function printWorkOrderPdf(
  workOrderId: string,
  options: WorkOrderPdfOptions = {},
  preOpenedWindow?: Window | null,
): Promise<void> {
  try {
    const blob = await api.getBlob(workOrderPdfPath(workOrderId, options));
    assertWorkOrderPdfBlob(blob);
    const objectUrl = URL.createObjectURL(blob);
    let popup: Window | null;
    if (preOpenedWindow !== undefined) {
      popup = preOpenedWindow;
      if (popup) popup.location.href = objectUrl;
    } else {
      popup = window.open(objectUrl, '_blank');
    }
    if (popup) {
      const target = popup;
      const trigger = (): void => {
        try { target.print(); } catch { /* print may be blocked until the PDF loads */ }
      };
      target.addEventListener('load', trigger);
      window.setTimeout(trigger, 500);
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    preOpenedWindow?.close();
    throw error;
  }
}

/**
 * Open the price-free delivery note ("Lähete") PDF in a tab (#4111). Same
 * popup rules as {@link printWorkOrderPdf}: pass a window opened in the click
 * handler; it is closed again if the download or the PDF check fails.
 */
export async function openWorkOrderDeliveryNotePdf(
  workOrderId: string,
  preOpenedWindow?: Window | null,
): Promise<void> {
  const popup = preOpenedWindow !== undefined ? preOpenedWindow : window.open('', '_blank');
  try {
    const blob = await api.getBlob(workOrderPdfPath(workOrderId, { variant: 'delivery_note' }));
    assertWorkOrderPdfBlob(blob);
    const objectUrl = URL.createObjectURL(blob);
    if (popup) {
      popup.location.href = objectUrl;
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.click();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    popup?.close();
    throw error;
  }
}
